import { useSearch } from '@tanstack/react-router'
import {
  Activity,
  BarChart3,
  CreditCard,
  DollarSign,
  FileCheck,
  FileText,
  Receipt,
  User,
  Wallet,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  SubtleTabs,
  SubtleTabsContent,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import { ComponentErrorBoundary } from '@/shared/components/errors/ErrorBoundary'
import type { CustomerResponse } from '../services/customerService'
import { ContractTab } from './tabs/ContractTab'
import { CostTab } from './tabs/CostTab'
import { CustomerDetailsTab } from './tabs/CustomerDetailsTab'
import { InvoiceTab } from './tabs/InvoiceTab'
import { LiveBillTab } from './tabs/LiveBillTab'
import { OverviewTab } from './tabs/OverviewTab'
import { PaymentsTab } from './tabs/PaymentsTab'
import { UsageTab } from './tabs/UsageTab'
import { ValueReceiptsTab } from './tabs/ValueReceiptsTab'
import { WalletTab } from './tabs/WalletTab'

interface CustomerDetailTabsProps {
  customer: CustomerResponse
  onCustomerUpdated?: (customer: CustomerResponse) => void
}

export function CustomerDetailTabs({
  customer,
  onCustomerUpdated,
}: CustomerDetailTabsProps) {
  const search = useSearch({ from: '/_auth/customers/$customerId/' })
  const [activeTab, setActiveTab] = useState('overview')

  // Set initial tab from URL parameter
  useEffect(() => {
    const urlTab = (search as Record<string, unknown>)?.tab
    if (
      typeof urlTab === 'string' &&
      [
        'overview',
        'contracts',
        'invoices',
        'payments',
        'wallet',
        'usage',
        'cost',
        'billing',
        'value-receipts',
        'details',
      ].includes(urlTab)
    ) {
      setActiveTab(urlTab)
    }
  }, [search])

  return (
    <SubtleTabs
      value={activeTab}
      onValueChange={setActiveTab}
      className='space-y-6'
    >
      <SubtleTabsList className='w-full overflow-x-auto'>
        <SubtleTabsTrigger value='overview' className='flex items-center gap-2'>
          <BarChart3 className='w-4 h-4' />
          <span>Overview</span>
        </SubtleTabsTrigger>
        <SubtleTabsTrigger
          value='contracts'
          className='flex items-center gap-2'
        >
          <FileText className='w-4 h-4' />
          <span>Contracts</span>
        </SubtleTabsTrigger>
        <SubtleTabsTrigger value='invoices' className='flex items-center gap-2'>
          <Receipt className='w-4 h-4' />
          <span>Invoices</span>
        </SubtleTabsTrigger>
        <SubtleTabsTrigger value='payments' className='flex items-center gap-2'>
          <CreditCard className='w-4 h-4' />
          <span>Payments</span>
        </SubtleTabsTrigger>
        <SubtleTabsTrigger value='wallet' className='flex items-center gap-2'>
          <Wallet className='w-4 h-4' />
          <span>Wallet</span>
        </SubtleTabsTrigger>
        <SubtleTabsTrigger value='usage' className='flex items-center gap-2'>
          <Activity className='w-4 h-4' />
          <span>Usage</span>
        </SubtleTabsTrigger>
        <SubtleTabsTrigger value='cost' className='flex items-center gap-2'>
          <DollarSign className='w-4 h-4' />
          <span>Cost</span>
        </SubtleTabsTrigger>
        <SubtleTabsTrigger value='billing' className='flex items-center gap-2'>
          <DollarSign className='w-4 h-4' />
          <span>Billing</span>
        </SubtleTabsTrigger>
        <SubtleTabsTrigger
          value='value-receipts'
          className='flex items-center gap-2'
        >
          <FileCheck className='w-4 h-4' />
          <span>Value Receipts</span>
        </SubtleTabsTrigger>
        <SubtleTabsTrigger value='details' className='flex items-center gap-2'>
          <User className='w-4 h-4' />
          <span>Details</span>
        </SubtleTabsTrigger>
      </SubtleTabsList>

      <SubtleTabsContent value='overview' className='space-y-4'>
        <ComponentErrorBoundary componentName='OverviewTab'>
          <OverviewTab customer={customer} />
        </ComponentErrorBoundary>
      </SubtleTabsContent>

      <SubtleTabsContent value='contracts' className='space-y-4'>
        <ComponentErrorBoundary componentName='ContractTab'>
          <ContractTab customer={customer} />
        </ComponentErrorBoundary>
      </SubtleTabsContent>

      <SubtleTabsContent value='invoices' className='space-y-4'>
        <ComponentErrorBoundary componentName='InvoiceTab'>
          <InvoiceTab customer={customer} />
        </ComponentErrorBoundary>
      </SubtleTabsContent>

      <SubtleTabsContent value='payments' className='space-y-4'>
        <ComponentErrorBoundary componentName='PaymentsTab'>
          <PaymentsTab customer={customer} />
        </ComponentErrorBoundary>
      </SubtleTabsContent>

      <SubtleTabsContent value='wallet' className='space-y-4'>
        <ComponentErrorBoundary componentName='WalletTab'>
          <WalletTab customer={customer} />
        </ComponentErrorBoundary>
      </SubtleTabsContent>

      <SubtleTabsContent value='usage' className='space-y-4'>
        <ComponentErrorBoundary componentName='UsageTab'>
          <UsageTab customer={customer} />
        </ComponentErrorBoundary>
      </SubtleTabsContent>

      <SubtleTabsContent value='cost' className='space-y-4'>
        <ComponentErrorBoundary componentName='CostTab'>
          <CostTab customer={customer} />
        </ComponentErrorBoundary>
      </SubtleTabsContent>

      <SubtleTabsContent value='billing' className='space-y-4'>
        <ComponentErrorBoundary componentName='LiveBillTab'>
          <LiveBillTab customer={customer} />
        </ComponentErrorBoundary>
      </SubtleTabsContent>

      <SubtleTabsContent value='value-receipts' className='space-y-4'>
        <ComponentErrorBoundary componentName='ValueReceiptsTab'>
          <ValueReceiptsTab customer={customer} />
        </ComponentErrorBoundary>
      </SubtleTabsContent>

      <SubtleTabsContent value='details' className='space-y-4'>
        <ComponentErrorBoundary componentName='CustomerDetailsTab'>
          <CustomerDetailsTab
            customer={customer}
            onCustomerUpdated={onCustomerUpdated}
          />
        </ComponentErrorBoundary>
      </SubtleTabsContent>
    </SubtleTabs>
  )
}
